import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { merge, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  skip,
  switchMap,
  tap,
} from 'rxjs/operators';
import { LogMiddlewareServiceAttributes } from '@albatrosdeveloper/ave-models-package';
import {
  createPageStateSignals,
  createPageActionSubjects,
} from 'app/shared/patterns/crud';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { SystemLatencyService } from '../../system-latency/system-latency.service';

export interface IntegrationErrorsColumnFilters {
  code?: string;
  type?: string;
  companyName?: string;
}

interface IntegrationErrorsQueryParams {
  search: string;
  pageNum: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  columnFilters: IntegrationErrorsColumnFilters;
}

const DEFAULT_QUERY_PARAMS: IntegrationErrorsQueryParams = {
  search: '',
  pageNum: 1,
  pageSize: 25,
  sortBy: 'dateStart',
  sortOrder: 'DESC',
  columnFilters: {},
};

/**
 * Page service del modal de errores de integración SAP (badge del header).
 * Sigue el patrón canónico de listados en modal (ver
 * `MaterialUsagePageService`): estado en signals (`createPageStateSignals`) y
 * acciones del usuario en Subjects (`createPageActionSubjects`); el fetch se
 * dispara con `toObservable(params)` + `switchMap` contra
 * `SystemLatencyService.findRecentErrors` (status = ERROR del día actual,
 * orden por `dateStart` descendente). Provisto a nivel del componente del modal.
 */
@Injectable()
export class IntegrationErrorsPageService {
  private readonly latencyService = inject(SystemLatencyService);
  private readonly handlerMessage = inject(HandlerMessageService);

  private readonly state = createPageStateSignals<LogMiddlewareServiceAttributes>();
  private readonly actions = createPageActionSubjects();

  private readonly queryParams = signal<IntegrationErrorsQueryParams>(DEFAULT_QUERY_PARAMS);

  // ─── Acciones del usuario (Subjects) ───────────────────────────────────────
  readonly setSearch$ = this.actions.setSearch$;
  readonly setPagination$ = this.actions.setPagination$;
  readonly setSort$ = this.actions.setSort$;
  readonly reload$ = this.actions.reload$;

  // ─── Estado (signals) ──────────────────────────────────────────────────────
  readonly data = computed(() => this.state._data());
  readonly totalCount = computed(() => this.state._totalCount());
  readonly isLoading = computed(() => this.state._isLoading());
  readonly error = computed(() => this.state._error());

  readonly pageNum = computed(() => this.queryParams().pageNum);
  readonly pageSize = computed(() => this.queryParams().pageSize);
  readonly pageIndex = computed(() => this.queryParams().pageNum - 1);
  readonly sortState = computed(() => ({
    column: this.queryParams().sortBy ?? null,
    direction: this.queryParams().sortOrder ?? null,
  }));

  constructor() {
    this.setupUserActionsSync();
    this.setupDataFetching();
  }

  /** Dispara el primer fetch (llamar desde el `ngOnInit` del modal). */
  init(): void {
    this.reload$.next();
  }

  /** Filtros por columna (modo api de la grilla) → cláusulas de buildQuery. */
  setColumnFilters(filters: IntegrationErrorsColumnFilters): void {
    this.queryParams.update((p) => ({ ...p, columnFilters: filters, pageNum: 1 }));
  }

  private setupUserActionsSync(): void {
    this.setSearch$
      .pipe(takeUntilDestroyed(), debounceTime(300), distinctUntilChanged())
      .subscribe((search) =>
        this.queryParams.update((p) => ({ ...p, search, pageNum: 1 }))
      );

    this.setPagination$
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(
          (a, b) => a.pageNum === b.pageNum && a.pageSize === b.pageSize
        )
      )
      .subscribe(({ pageNum, pageSize }) =>
        this.queryParams.update((p) => ({ ...p, pageNum, pageSize }))
      );

    this.setSort$.pipe(takeUntilDestroyed()).subscribe((event) =>
      this.queryParams.update((p) => ({
        ...p,
        sortBy: event.direction ? event.column ?? undefined : undefined,
        sortOrder: event.direction ?? undefined,
        pageNum: 1,
      }))
    );
  }

  private setupDataFetching(): void {
    const params$ = toObservable(this.queryParams).pipe(
      skip(1),
      debounceTime(100),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    );

    const reload$ = this.reload$.pipe(map(() => this.queryParams()));

    merge(params$, reload$)
      .pipe(
        takeUntilDestroyed(),
        tap(() => {
          this.state._isLoading.set(true);
          this.state._error.set(null);
        }),
        switchMap((p) =>
          this.latencyService
            .findRecentErrors(
              {
                search: p.search || undefined,
                currentPage: p.pageNum,
                limitPerPage: p.pageSize,
                sort: p.sortBy as keyof LogMiddlewareServiceAttributes,
                sortDirection: p.sortOrder,
              },
              p.columnFilters
            )
            .pipe(
              catchError((err) => {
                this.state._error.set(
                  err?.error?.message || 'Error al cargar los errores de integración.'
                );
                this.handlerMessage.error({
                  message: err?.error?.message,
                  alternativeMessage: 'Error al cargar los errores de integración',
                  statusCode: err?.status,
                });
                return of(null);
              })
            )
        )
      )
      .subscribe((res) => {
        if (res) {
          this.state._data.set(res.data?.[0]?.data ?? []);
          this.state._totalCount.set(res.data?.[0]?.pageInfo?.[0]?.total ?? 0);
        } else {
          this.state._data.set([]);
          this.state._totalCount.set(0);
        }
        this.state._isLoading.set(false);
      });
  }
}
